import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { studentMultipleChoicesAPI } from "../../redux/features/courseSlice";
import AssignmentTimerCompleteModal from "../assignmentTimerCompleteModal/AssignmentTimerCompleteModal";
import AssignmentConfirmSubmitModal from "../AssignmentConfirmSubmitModal/AssignmentConfirmSubmitModal";

const MultipleChoiceAssignment = ({ allData, assignmentCompleteFunc }) => {
  const [questions, setQuestions] = useState([]);
  const [counter, setCounter] = useState(0);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [showTimerModal, setShowTimerModal] = useState(false);
  const [timerStarted, setTimerStarted] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const auth = useSelector((state) => state.auth);
  const [showModal, setShowModal] = useState(false);
  const [nextLocation, setNextLocation] = useState(null);
  const [fieldDisabled, setFieldDisabled] = useState(false);
  const [confirmedNavigation, setConfirmedNavigation] = useState(false);

  // Initialize formik before useEffect hooks
  const formik = useFormik({
    initialValues: {
      answers: questions.reduce((acc, question) => {
        acc[question.question_id] = "";
        return acc;
      }, {}),
      agreement: false,
    },
    // validationSchema: Yup.object({
    //   answers: Yup.object().shape(
    //     questions.reduce((acc, question) => {
    //       acc[question.question_id] = Yup.string().required(
    //         "This question is required"
    //       );
    //       return acc;
    //     }, {})
    //   ),
    // }),
    enableReinitialize: true,
    onSubmit: (values, { setSubmitting }) => {
      const submittedData = {
        answers: questions.map((question) => ({
          question_id: question.question_id,
          user_answer: Number(values.answers[question.question_id]) || null,
        })),
        agreement: values.agreement,
      };

      submitHandler(submittedData);
      setFormSubmitted(true);
      setSubmitting(false);
    },
  });

  const submitHandler = async (values) => {
    try {
      const response = await dispatch(
        studentMultipleChoicesAPI({
          data: values?.answers,
          token: auth.token,
          assignment_Id: searchParams.get("id"),
        })
      ).unwrap();
      if (response.success) {
        assignmentCompleteFunc();
      }
      setFormSubmitted(true);
    } catch (error) {
      setFormSubmitted(false);
    }
  };

  useEffect(() => {
    if (searchParams.get("view") === "true") {
      setFieldDisabled(true);
    } else {
      setFieldDisabled(false);
    }
  }, []);

  const goBack = () => {
    navigate(-1);
  };

  // Initialize questions
  useEffect(() => {
    if (allData?.questions) {
      const dataList = allData.questions.map((item) => ({
        question_id: item.id,
        question_text: item.question_text,
        options: item.AssignmentOptions || [],
        points: item.points || 1,
        user_answer: "",
      }));
      setQuestions(dataList);
    }
  }, [allData?.questions]);

  // Initialize timer
  useEffect(() => {
    if (allData?.duration) {
      setCounter(allData.duration * 60);
      setTimerStarted(true);
    } else {
      setTimerStarted(false);
    }
  }, [allData?.duration]);

  // Timer countdown
  useEffect(() => {
    if (searchParams.get("view") === "true" || !timerStarted) {
      return;
    }

    const interval = setInterval(() => {
      setCounter((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          if (!formSubmitted) {
            formik.handleSubmit(); // Auto-submit when timer reaches zero
            setShowTimerModal(true); // Show modal after submission
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [timerStarted, formSubmitted]); // Removed formik from dependencies

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${remainingSeconds
      .toString()
      .padStart(2, "0")} min`;
  };

  const handleConfirmLeave = () => {
    setShowModal(false);
    setConfirmedNavigation(true);
    if (nextLocation) {
      nextLocation.retry();
    }
  };

  const handleCancelLeave = () => {
    setShowModal(false);
    setNextLocation(null);
  };

  const handleTimerContinue = () => {
    setShowTimerModal(false);
    navigate(-1);
  };

  const handleSubmitConfirm = () => {
    setShowSubmitModal(false);
    formik.handleSubmit();
    navigate(-1);
  };

  const handleSubmitCancel = () => {
    setShowSubmitModal(false);
  };

  const handleManualSubmit = (e) => {
    e.preventDefault();
    setShowSubmitModal(true);
  };

  return (
    <>
      <div className="assignment-header mb-10 mb-sm-20 mb-md-24">
        <h3 className="ah-heading-04 fw-bold">{allData?.title}</h3>
        <p>{allData?.description}</p>
        <div className="item ah-text-md fw-bold theme-color text-end">
          Duration:{" "}
          <span className="fw-semibold black-100 opacity-50">
            {formatTime(counter)}
          </span>
        </div>
      </div>

      <div className="card-default-style px-12 px-sm-20 px-md-30 py-12 overflow-x-hidden">
        <div className="assignment">
          <div className="assignment-inner px-5 py-5">
            <form onSubmit={handleManualSubmit}>
              <ul className="p-0 list-unstyled mb-0">
                {questions?.map((question, index) => (
                  <li key={question.question_id}>
                    <div className="d-flex align-items-start justify-content-between flex-column-reverse flex-sm-row gap-2 gap-lg-3 gap-xl-5">
                      <div className="question mb-10 mb-sm-20 mb-md-32">
                        <div className="question-title ah-text-md fw-bold mb-14">
                          {index + 1}. {question.question_text}
                        </div>
                        {question?.options?.map((item, idx) => (
                          <label
                            key={idx}
                            className="custom-radio ah-text-base fw-normal ps-30 mb-14 d-block position-relative"
                          >
                            {item.option_text}
                            <input
                              type="radio"
                              name={`answers.${question.question_id}`}
                              value={idx + 1}
                              checked={
                                Number(
                                  formik.values.answers[question.question_id]
                                ) ===
                                idx + 1
                              }
                              disabled={fieldDisabled ? true : false}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                            />
                            <span className="checkmark"></span>
                          </label>
                        ))}
                        {formik.touched.answers?.[question.question_id] &&
                          formik.errors.answers?.[question.question_id] && (
                            <div className="text-danger ah-text-sm">
                              {formik.errors.answers[question.question_id]}
                            </div>
                          )}
                      </div>
                      <span className="points flex-shrink-0 h-auto px-29 py-8 ah-text-xs fw-semibold">
                        {question.points} Point
                        {question.points !== 1 ? "s" : ""}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>

              <div className="mt-20">
                {fieldDisabled ? (
                  <button
                    className="btn btn-primary no-shadow px-50 py-15"
                    type="button"
                    onClick={goBack}
                  >
                    Back
                  </button>
                ) : (
                  <button
                    className="btn btn-primary no-shadow px-50 py-15"
                    type="submit"
                    disabled={formik.isSubmitting}
                  >
                    {formik.isSubmitting ? "Submitting..." : "Submit"}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
        <ConfirmLeaveModal
          show={showModal}
          onConfirm={handleConfirmLeave}
          onCancel={handleCancelLeave}
        />
        <AssignmentTimerCompleteModal
          show={showTimerModal}
          onContinue={handleTimerContinue}
        />
        <AssignmentConfirmSubmitModal
          show={showSubmitModal}
          onConfirm={handleSubmitConfirm}
          onCancel={handleSubmitCancel}
        />
      </div>
    </>
  );
};

const ConfirmLeaveModal = ({ show, onConfirm, onCancel }) => {
  if (!show) return null;

  return (
    <div className="modal-backdrop">
      <div className="modal-box">
        <h4>Leave Page?</h4>
        <p>Your answers will be lost. Are you sure you want to leave?</p>
        <div className="modal-actions">
          <button className="btn btn-danger" onClick={onConfirm}>
            Leave
          </button>
          <button className="btn btn-secondary" onClick={onCancel}>
            Stay
          </button>
        </div>
      </div>
    </div>
  );
};

export default MultipleChoiceAssignment;
